/* ============================================================
   notificationsView — CB-2 (286c2f72): pure view helpers for the
   notification bell/drawer. No React, fully node-testable:
   relative ru timestamps, badge caps, empty/copy states, paging.
   ============================================================ */

import type { NotificationItem } from '../types/notification';
import type { AdminTab, Page } from '../routing';
import type { ProviderTab } from '../types/provider';

/** Relative ru timestamp: «только что» → «N мин назад» → «N ч назад» → «вчера» → «D.M». */
export function notificationTimeLabel(iso: string, now: Date = new Date()): string {
  const created = new Date(iso);
  if (Number.isNaN(created.getTime())) return '';
  const ms = Math.max(0, now.getTime() - created.getTime());
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 1) return 'только что';
  if (minutes < 60) return `${minutes} мин назад`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} ч назад`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'вчера';
  if (days < 7) return `${days} дн назад`;
  const d = created.getDate();
  const m = created.getMonth() + 1;
  const y = created.getFullYear();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const sameYear = created.getFullYear() === today.getFullYear();
  return sameYear ? `${d}.${m}` : `${d}.${m}.${y}`;
}

/** Badge text: 0 → empty (hidden), 1..99 → as-is, ≥100 → «99+». */
export function unreadBadgeText(count: number): string {
  if (count <= 0) return '';
  return count > 99 ? '99+' : String(count);
}

/** Icon per kind group — light glyphs, no color noise (design system). */
export function notificationIcon(kind: string): string {
  switch (kind) {
    case 'OrderCreated':
    case 'NewOrderForProvider':
      return '🧾';
    case 'OrderAccepted':
      return '✅';
    case 'OrderShipped':
      return '📦';
    case 'OrderReady':
      return '🎉';
    case 'OrderCancelled':
    case 'ResultDisputed':
    case 'CounterOfferRejected':
      return '⚠️';
    case 'DisputeResolved':
      return '🛡️';
    case 'ResultReady':
      return '📷';
    case 'PrintingStarted':
      return '🖨️';
    case 'ResultConfirmed':
    case 'ResultAutoConfirmed':
      return '👍';
    case 'CounterOfferProposed':
    case 'CounterOfferAccepted':
    case 'OrderChatMessage':
      return '💬';
    case 'DisputeStaleAdmin':
      return '⏳';
    case 'DisputeStaleEscalatedAdmin':
      return '🚨';
    case 'PayoutFailed':
    case 'PayoutMethodMissing':
      return '💳';
    case 'SafeDealOpsAttention':
      return '📊';
    default:
      return '🔔';
  }
}

/**
 * s9 (d2de0c1c, audit-6 GAP-C): where tapping a notification row navigates.
 * null = inert row (unknown kind, or a dual-recipient kind whose leg can't
 * be resolved from the session role).
 */
export interface NotificationTarget {
  page: Page;
  /** Per-order Guid focus when page === 'orders'; null = plain list. */
  orderId: string | null;
  /** Admin tab focus when page === 'admin' (disputes for stale-dispute push). */
  adminTab: AdminTab | null;
  /** F3 (268dfd51): provider tab focus when page === 'provider' — every
   *  actionable provider push lands on the «Заказы» queue, not Профиль. */
  providerTab: ProviderTab | null;
}

/**
 * s9 (d2de0c1c): pure kind→surface map.
 *
 * Customer-only kinds deep-link to the order card (/orders with the row's
 * orderId as focus). Provider-only kinds (NewOrderForProvider,
 * ResultConfirmed, the SD-4 payout kinds PayoutFailed/PayoutMethodMissing)
 * open the portal. The RC-1a stale-dispute admin kinds
 * (DisputeStaleAdmin, DisputeStaleEscalatedAdmin) open /admin/disputes.
 *
 * Dual-recipient kinds — ResultDisputed, DisputeResolved and the
 * CounterOffer* family (the same Kind value is written to the customer and
 * to the provider) — resolve by the *recipient's* session role, mirroring
 * the s8 per-recipient email CTA rule: Customer leg → order card, Provider
 * leg → portal. Kind alone can't tell the legs apart, and a kind-only rule
 * would repeat audit-6 GAP-A/GAP-B inside the app (customer dumped on the
 * provider portal). Unresolvable roles stay inert.
 *
 * F3 (268dfd51): every provider-leg target now carries providerTab 'orders'
 * — the actionable surface (accept/finish/dispute lives in the queue),
 * while payout kinds land where the payout card is reachable from the
 * queue's own navigation (still 'orders': the provider-act queue tab).
 */
export function notificationTarget(
  kind: string,
  role: string | null = null,
): NotificationTarget | null {
  switch (kind) {
    case 'OrderCreated':
    case 'OrderAccepted':
    case 'OrderShipped':
    case 'OrderReady':
    case 'OrderCancelled':
    case 'ResultReady':
    case 'ResultAutoConfirmed':
    case 'PrintingStarted':
      return { page: 'orders', orderId: null, adminTab: null, providerTab: null };
    case 'NewOrderForProvider':
    case 'ResultConfirmed':
    case 'PayoutFailed':
    case 'PayoutMethodMissing':
      return { page: 'provider', orderId: null, adminTab: null, providerTab: 'orders' };
    case 'DisputeStaleAdmin':
    case 'DisputeStaleEscalatedAdmin':
      return { page: 'admin', orderId: null, adminTab: 'disputes', providerTab: null };
    case 'SafeDealOpsAttention':
      // SD-6: money-ops attention — the report endpoint is the detail
      // surface; the admin panel root is the working surface (orders).
      return { page: 'admin', orderId: null, adminTab: null, providerTab: null };
    case 'ResultDisputed':
    case 'DisputeResolved':
    case 'CounterOfferProposed':
    case 'CounterOfferAccepted':
    case 'CounterOfferRejected':
    // d0add266 (audit-11 §5 Card A): chat messages are dual-recipient by
    // nature (either party can write) — same per-role rule: the provider
    // answers from the portal queue, the customer from the order card
    // (both surfaces host the chat thread).
    case 'OrderChatMessage':
      if (role === 'Provider') return { page: 'provider', orderId: null, adminTab: null, providerTab: 'orders' };
      if (role === 'Customer') return { page: 'orders', orderId: null, adminTab: null, providerTab: null };
      return null;
    default:
      return null;
  }
}

/** Row presentation for the drawer list. */
export interface NotificationRowView {
  id: string;
  icon: string;
  title: string;
  body: string;
  timeLabel: string;
  unread: boolean;
  orderNumber: string | null;
  /** s9: deep-link target for the row tap; null = inert row. */
  target: NotificationTarget | null;
}

export function notificationRowView(
  item: NotificationItem,
  now: Date = new Date(),
  role: string | null = null,
): NotificationRowView {
  // Compose the kind map with the row's own orderId: orders-surface rows
  // focus the exact order the notification is about (a missing Guid falls
  // back to the plain customer list — still the right surface).
  const mapped = notificationTarget(item.kind, role);
  const target =
    mapped !== null && mapped.page === 'orders' ? { ...mapped, orderId: item.orderId } : mapped;  return {
    id: item.id,
    icon: notificationIcon(item.kind),
    title: item.title,
    body: item.body,
    timeLabel: notificationTimeLabel(item.createdAtUtc, now),
    unread: item.readAtUtc === null,
    orderNumber: item.orderNumber,
    target,
  };
}

/**
 * s9: rendered-row interactivity for the drawer — a row responds to taps
 * only when it has a target AND the drawer is wired to an onNavigate
 * handler. Pure so the drawer markup stays a thin wrapper (node-testable).
 */
export function drawerRowPress(
  row: NotificationRowView,
  onNavigateWired: boolean,
): { active: boolean; target: NotificationTarget | null } {
  return { active: row.target !== null && onNavigateWired, target: row.target };
}

/** Drawer footer text: «Показано X из Y» / empty list copy. */
export function notificationFooterText(totalCount: number, shown: number): string {
  return `Показано ${shown} из ${totalCount}`;
}

/** Empty-state copy depends on whether anything was ever delivered. */
export function notificationEmptyText(hasAny: boolean): string {
  return hasAny
    ? 'Все уведомления прочитаны'
    : 'Уведомлений пока нет — здесь появятся статусы ваших заказов';
}

/** «Показать ещё» visibility: only when the page did not exhaust the feed. */
export function canLoadMore(totalCount: number, shown: number): boolean {
  return shown < totalCount;
}

/** Read-all button state: pointless (and hidden) with nothing unread. */
export function canMarkAllRead(unreadCount: number): boolean {
  return unreadCount > 0;
}
